import { DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { defaultDateDef, simpleNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { DateCellEditor } from '@components/_SiteWide/Grid/CellEditors/DateCellEditor'
import { Status } from '@components/_SiteWide/Grid/CellRenderers/CardingCredentialsStatus'
import { CustomHeaderTooltips } from '@components/_SiteWide/Grid/CustomHeaderTooltips'
import { addCommasToNumber, GraviButton, Texto } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Popconfirm, Tooltip } from 'antd'
import React from 'react'

export const getColumnDefs = (handleDelete) => [
  {
    headerName: 'Depot',
    field: 'depot',
    rowGroup: true,
    rowGroupIndex: 1,
    editable: false,
    initialHide: true,
  },
  {
    headerName: 'Driver',
    field: 'driver',
    rowGroup: true,
    rowGroupIndex: 2,
    editable: false,
    initialHide: true,
  },
  {
    headerName: 'Terminal Name',
    field: 'terminal',
    rowGroup: true,
    editable: false,
    initialHide: true,
  },
  {
    headerName: 'Card Name',
    field: 'card_name',
    cellRenderer: (params) => {
      if (params.node.group) {
        return params.value
      }

      return (
        <Tooltip title={params.data?.carding_instructions} placement='topLeft'>
          <span>{params.data?.card_name}</span>
        </Tooltip>
      )
    },
    editable: false,
    headerTooltip: true,
    tooltipComponent: (params) => (
      <CustomHeaderTooltips
        displayValue={() => (
          <>
            <Texto weight='bold'>Card Name</Texto>
            <Texto>Cards required for loading and unloading activities at Terminals.</Texto>
            <Texto>Ex. Loading Card 90, Unloading Card 120</Texto>
          </>
        )}
      />
    ),
  },
  {
    headerName: 'Certification Date',
    field: 'certification_date',
    editable: true,
    cellEditor: DateCellEditor,
    cellEditorParams: (params) => ({
      accessor: 'certification_date',
      disabledDate: (current) => current && current > new Date(),
    }),
    ...defaultDateDef('certification_date', formatters.DATE),
    headerTooltip: true,
    tooltipComponent: (params) => (
      <CustomHeaderTooltips
        displayValue={() => (
          <>
            <Texto weight='bold'>Certification Date</Texto>
            <Texto>Enter the date the Driver was last certified or last performed the certified activity.</Texto>
            <Texto>
              Cards with no certification date and/or days valid and Dispatch Impact is not set to Informational (No
              Impact) will alert dispatch.
            </Texto>
            <Texto> Cards with Dispatch Impact set to Informational (No Impact) will not alert dispatch.</Texto>
          </>
        )}
      />
    ),
  },
  {
    headerName: 'Status',
    field: 'status',
    aggFunc: (props) => props.values,
    cellRenderer: Status,
    // initialWidth: 120,
    minWidth: 140,
    editable: false,
    headerTooltip: true,
    tooltipComponent: (params) => (
      <CustomHeaderTooltips
        displayValue={() => (
          <>
            <Texto weight='bold'>Status</Texto>
            <Texto>
              Valid - Certification date and days valid are populated, and it is not within warning threshold.
            </Texto>
            <Texto>Expires Soon - Expiration is within warning threshold.</Texto>
            <Texto>Expired - Expiration is beyond certification date</Texto>
            <Texto>
              Missing Date - No certification date and/or days valid and Dispatch Impact is not set to Informational
              Will still alert dispatch
            </Texto>
            <Texto>NA - No certification date and/or days valid and Dispatch Impact is set to Informational.</Texto>
          </>
        )}
      />
    ),
  },
  {
    headerName: 'Expiration Date',
    field: 'expiration_date',
    ...defaultDateDef('expiration_date', formatters.DATE),
    editable: false,
    headerTooltip: true,
    tooltipComponent: (params) => (
      <CustomHeaderTooltips
        displayValue={() => (
          <>
            <Texto weight='bold'>Expiration Date</Texto>
            <Texto>Auto updates based on Certification Date and Days Valid</Texto>
            <Texto>
              Cards with no expiration date and/or days valid and Dispatch Impact is not set to Informational (No
              Impact) will alert dispatch
            </Texto>
            <Texto>Cards with Dispatch Impact set to Informational (No Impact) will not alert dispatch.</Texto>
          </>
        )}
      />
    ),
  },
  {
    headerName: 'Days Valid',
    field: 'days_valid',
    editable: false,
    ...simpleNumberDef('days_valid', 0),
    headerTooltip: true,
    tooltipComponent: (params) => (
      <CustomHeaderTooltips
        displayValue={() => (
          <>
            <Texto weight='bold'>Days Valid</Texto>
            <Texto>Based on Certification Trigger</Texto>
            <Texto>Automatic: Days between activity before expiration</Texto>
            <Texto>Manual: Days after certification date before expiration</Texto>
            <Texto>
              Never Expires: Will clear Days Valid and Warning Threshold and set Dispatch Impact to "Informational (No
              Impact)
            </Texto>
          </>
        )}
      />
    ),
    valueGetter: (params) => {
      if (params.node.group || params.data.dispatch_impact === 'Informational (No Impact)') return null
      if (params.data.days_valid || params.data.days_valid === 0) return addCommasToNumber(params.data.days_valid, 0)
    },
    cellRenderer: (params) => {
      if (!params.node?.group && params.value) {
        const isLight = params.data.certification_trigger === 'Never Expires'
        return (
          <Texto style={{ color: isLight ? 'var(--gray-400)' : 'inherit' }} align='right'>
            {addCommasToNumber(params.value, 0)}
          </Texto>
        )
      }
      return addCommasToNumber(params.value, 0)
    },
  },
  {
    headerName: 'Warning Threshold',
    field: 'warning_threshold',
    ...simpleNumberDef('warning_threshold', 0),
    cellStyle: () => ({ textAlign: 'right' }),
    headerTooltip: true,
    editable: false,
    tooltipComponent: (params) => (
      <CustomHeaderTooltips
        displayValue={() => (
          <>
            <Texto weight='bold'>Warning Threshold</Texto>
            <Texto>Days before Expiration to Trigger Warning</Texto>
          </>
        )}
      />
    ),
    valueGetter: (params) => {
      if (params.node.group || params.data.dispatch_impact === 'Informational (No Impact)') return null

      if (params?.data?.warning_threshold || params?.data?.warning_threshold === 0)
        return addCommasToNumber(params?.data?.warning_threshold, 0)
    },
    cellRenderer: (params) => {
      if (!params.node?.group && params.value) {
        const isLight = params.data.certification_trigger === 'Never Expires'
        return (
          <Texto style={{ color: isLight ? 'var(--gray-400)' : 'inherit' }} align='right'>
            {addCommasToNumber(params.value, 0)}
          </Texto>
        )
      }
      return addCommasToNumber(params.value, 0)
    },
  },
  {
    headerName: 'Activity',
    field: 'activity',
    headerTooltip: true,
    editable: false,
    tooltipComponent: (params) => (
      <CustomHeaderTooltips
        displayValue={() => (
          <>
            <Texto weight='bold'>Activity</Texto>
            <Texto>Loading - references loading activity (i.e. BOLs)</Texto>
            <Texto> Unloading - references unloading activity (i.e. Backhauls)</Texto>
          </>
        )}
      />
    ),
  },
  {
    headerName: 'Certification Trigger',
    field: 'certification_trigger',
    headerTooltip: true,
    editable: false,
    tooltipComponent: (params) => (
      <CustomHeaderTooltips
        displayValue={() => (
          <>
            <Texto weight='bold'>Certification Trigger</Texto>
            <Texto>Automatic - Activity Based: certification triggers based on days between Activity</Texto>
            <Texto>Manual - Date Based: certification triggers based on days certification date</Texto>
            <Texto>Never Expires: never expires and is set to Dispatch Impact "Informational (No Impact)</Texto>
            <Texto>Unloading - references unloading activity (i.e. Backhauls)</Texto>
          </>
        )}
      />
    ),
  },
  {
    headerName: 'Dispatch Impact',
    field: 'dispatch_impact',
    minWidth: 200,
    headerTooltip: true,
    editable: false,
    cellStyle: (params) => {
      const dispatchImpact = params.data?.dispatch_impact
      const bgColor =
        dispatchImpact === 'Block Dispatch'
          ? 'var(--theme-error-dim)'
          : dispatchImpact === 'Informational (No Impact)'
            ? 'var(--theme-color-2-dim)'
            : dispatchImpact?.includes('Alert')
              ? 'var(--theme-warning-dim)'
              : ''
      return { backgroundColor: bgColor }
    },
    tooltipComponent: (params) => (
      <CustomHeaderTooltips
        displayValue={() => (
          <>
            <Texto weight='bold'>Dispatch Impact</Texto>
            <Texto>
              If orders or backhauls are attempted to be dispatched to a driver with an expired card or credential then:
            </Texto>
            <Texto>Alert: Alert on Driver Dispatch Driver Card and can dispatch</Texto>
            <Texto>
              Alert Manual, Block Optimizer: Alert on Driver Dispatch Driver Card of manually dispatched orders but
              blocks auto dispatching by Route Optimizer Missing certification date will alert dispatch
            </Texto>
            <Texto>Block: Blocks dispatch with error msg</Texto>
            <Texto>Informational (No Impact): No alert or blocking dispatch</Texto>
          </>
        )}
      />
    ),
  },
  {
    pinned: 'right',
    headerName: 'Actions',
    editable: false,
    width: 120,
    cellRenderer: (params) => {
      if (params.node?.group) return null
      return (
        <Popconfirm
          title={`Removing terminal card from ${params?.data?.driver || 'this driver'}.  Continue?`}
          onConfirm={() => handleDelete(params.data)}
          okText='Confirm'
          icon={<QuestionCircleOutlined style={{ color: 'var(--theme-error)' }} />}
        >
          <GraviButton icon={<DeleteOutlined />} error />
        </Popconfirm>
      )
    },
    headerTooltip: true,
    tooltipComponent: (params) => (
      <CustomHeaderTooltips displayValue={() => <Texto>Removes Card for that driver at that terminal only.</Texto>} />
    ),
  },
  { headerName: 'Market', field: 'market', initialHide: true, editable: false },
]
